import { getAllDemoData } from "./demoDataFormatted";

const DEMO_MODE_KEY = "finance_demo_mode";

export class DemoService {
  private static instance: DemoService;
  private isDemoMode: boolean = false;
  private listeners: ((isDemoMode: boolean) => void)[] = [];

  private constructor() {
    // Load demo mode state from localStorage
    const savedMode = localStorage.getItem(DEMO_MODE_KEY);
    this.isDemoMode = savedMode === "true";
  }

  static getInstance(): DemoService {
    if (!DemoService.instance) {
      DemoService.instance = new DemoService();
    }
    return DemoService.instance;
  }

  // Toggle demo mode on/off
  toggleDemoMode(): boolean {
    this.isDemoMode = !this.isDemoMode;
    localStorage.setItem(DEMO_MODE_KEY, this.isDemoMode.toString());
    console.debug("[DEBUG] Demo mode toggled", { isDemoMode: this.isDemoMode });

    // Clear session storage when toggling to ensure fresh data
    if (this.isDemoMode) {
      sessionStorage.removeItem("finance_demo_data");
    }

    // Notify all listeners of the change
    this.listeners.forEach((listener) => listener(this.isDemoMode));

    return this.isDemoMode;
  }

  // Explicitly set demo mode state
  setDemoMode(isDemoMode: boolean) {
    if (this.isDemoMode === isDemoMode) {
      return;
    }
    this.isDemoMode = isDemoMode;
    localStorage.setItem(DEMO_MODE_KEY, this.isDemoMode.toString());
    console.debug("[DEBUG] Demo mode set", { isDemoMode: this.isDemoMode });

    if (this.isDemoMode) {
      sessionStorage.removeItem("finance_demo_data");
    }

    this.listeners.forEach((listener) => listener(this.isDemoMode));
  }

  // Check if demo mode is active
  isDemo(): boolean {
    return this.isDemoMode;
  }

  // Subscribe to demo mode changes
  subscribe(listener: (isDemoMode: boolean) => void): () => void {
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  // Get demo data for a specific type
  getDemoData(
    type:
      | "expenses"
      | "income"
      | "debts"
      | "accounts"
      | "transactions"
      | "goals"
  ) {
    const allData = getAllDemoData();
    return allData[type] || [];
  }

  // Get all demo data
  getAllDemoData() {
    return getAllDemoData();
  }
}

// Export singleton instance
export const demoService = DemoService.getInstance();
